import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { esES } from "@mui/x-data-grid/locales";
import {
  GridRowModes,
  DataGrid,
  GridRowEditStopReasons,
} from "@mui/x-data-grid";
import { LGCrudToolbar } from "../../molecules/LGCrud/toolbar";
import LGCrudViewMode from "../../molecules/LGCrud/viewMode";
import LGCrudEditMode from "../../molecules/LGCrud/editMode";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import ConfirmDialog from "../../molecules/Common/ConfirmDialog";
import { useCompany } from "../../contexts/CompanyContext";
import { usePersistedPagination } from "../../hooks/usePersistedPagination";

export default function LGCrud(props) {
  const {
    columns,
    actionHandlers,
    catalogueName = null,
    parentId = null,
    clientSidePagination = false,
    customFetchDeps = [],
    dataGridProps = {},
    newRowDefaults = { name: "" },
    canEditRow = () => true,
    paginationKey,
    persistPagination,
  } = props;
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [openConfirm, setOpenConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const { openSnack } = useSnack();
  const [selectedId, setSelectedId] = useState();
  const { selectedCompany } = useCompany();
  // FILTER, SORT AND PAGINATION
  const [queryOptions, setQueryOptions] = useState({});
  const [queryParams, setQueryParams] = useState(catalogueName ? { catalogueName } : {});
  const shouldPersistPagination =
    persistPagination ?? !(parentId?.name && parentId.name !== "companyId");
  const [paginationModel, setPaginationModel] = usePersistedPagination({
    paginationKey: paginationKey || catalogueName || undefined,
    persistPagination: shouldPersistPagination,
    defaultPageSize: 20,
  });

  const onFilterChange = useCallback((filterModel) => {
    setQueryOptions({ filterModel: { ...filterModel } });
    const qp = filterModel.items.reduce((acc, item) => {
      if (item.value !== undefined && item.value !== "") {
        acc[item.field] = item.value;
      }
      return acc;
    }, {});
    setQueryParams((prev) => ({ ...prev, ...qp }));
  }, []);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setOpenConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const res = await actionHandlers.delete(selectedId);
      setRows(rows.filter((row) => row.id !== selectedId));
      openSnack("Entrada borrada exitosamente", "success");
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setOpenConfirm(false); // Close the dialog
    }
  };

  const handleCloseDialog = () => {
    setOpenConfirm(false);
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await actionHandlers.fetchData(queryParams);
      setRows(response.data);
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const processRowUpdate = async (newRow) => {
    const isNew = newRow.isNew;
    const payload = { ...newRow };
    delete payload.id;
    delete payload.isNew;

    let updatedRow = { ...newRow, isNew: false };

    if (isNew) {
      const res = await actionHandlers.create(payload);
      if (res && res.id != null) {
        updatedRow = { ...updatedRow, id: res.id };
      }
    } else {
      await actionHandlers.update(newRow.id, payload);
    }

    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  // Process columns to ensure proper edit mode handling
  const processedColumns = [
    ...columns.map(column => {
      // For singleSelect fields, ensure proper value handling in edit mode
      if (column.type === 'singleSelect' && column.valueGetter) {
        return {
          ...column,
          // Ensure the field value is properly set for edit mode
          preProcessEditCellProps: (params) => {
            // This ensures the edit field is initialized with the correct value
            return { ...params, hasChanged: false };
          },
          // Add proper edit field initialization
          getEditCellProps: (params) => {
            // Ensure the edit field shows the correct value
            const value = column.valueGetter ? column.valueGetter(params) : params.value;
            return {
              ...params,
              value: value,
            };
          },
        };
      }
      return column;
    }),
    {
      field: "actions",
      type: "actions",
      headerName: "Acciones",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <LGCrudEditMode
              id={id}
              onSave={handleSaveClick}
              onCancel={handleCancelClick}
            />,
          ];
        }

        return [
          <LGCrudViewMode
            id={id}
            onEdit={canEditRow(row) ? () => handleEditClick(id) : undefined}
            onDelete={() => handleDeleteClick(id)}
            onDetail={
              actionHandlers.onDetail
                ? (event) => actionHandlers.onDetail(event, id, row)
                : undefined
            }
          />,
        ];
      },
    },
  ];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData uses latest actionHandlers/queryParams via closure
  }, clientSidePagination
    ? [queryParams, ...customFetchDeps]
    : [queryParams, paginationModel, ...customFetchDeps]);

  useEffect(() => {
    if (clientSidePagination) return;
    setQueryParams((prev) => ({
      ...prev,
      pageNumber: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      companyId: selectedCompany.id,
      ...(parentId && { [parentId.name]: parentId.value }),
    }));
  }, [paginationModel, clientSidePagination, parentId]);

  return (
    <Box
      sx={{
        width: "100%",
        "& .actions": {
          color: "text.secondary",
        },
        "& .textPrimary": {
          color: "text.primary",
        },
      }}
    >
      <ConfirmDialog
        open={openConfirm}
        handleClose={handleCloseDialog}
        handleConfirm={handleConfirmDelete}
      />
      <DataGrid
        rows={rows}
        columns={processedColumns}
        getRowId={(row) => row.id}
        loading={loading}
        editMode="row"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        paginationMode={clientSidePagination ? "client" : "server"}
        filterMode={clientSidePagination ? "client" : "server"}
        {...(!clientSidePagination && {
          rowCount: -1,
          onFilterModelChange: onFilterChange,
        })}
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          console.log(error);
        }}
        slots={{
          toolbar: LGCrudToolbar,
        }}
        slotProps={{
          toolbar: { setRows, setRowModesModel, newRowDefaults },
        }}
        {...dataGridProps}
      />
    </Box>
  );
}
